import { useRef } from "react"

import { HelpCenterContent, HelpCenterHeader, WikiSection } from "@/areas/help-center"
import Article from "@/shared/layouts/Article/Article"

function OracleSetup() {
  const contentRef = useRef<HTMLDivElement>(null)

  return (
    <HelpCenterContent ref={contentRef}>
      <HelpCenterHeader
        title="Oracle Setup"
        subtitle="What you need to configure inside ORACLE POS so it can send orders correctly."
        logo="/static/wiki/oracle/oracle-logo.png"
        backPath="/help-center/pos-configuration"
        backLabel="POS Configuration Guide"
        contentRef={contentRef}
      />

      <WikiSection id="overview" title="OVERVIEW">
        <Article>
          <p>This document provides a step-by-step process for:</p>
          <ul className="wiki-list">
            <li>Configuring the Stream Extension App inside <strong>EMC</strong></li>
            <li>Connecting Oracle to your Stream-Editor location</li>
            <li>Linking Oracle POS with the OCB Menu App</li>
          </ul>

          <h3>Requirements</h3>
          <ul className="wiki-list">
            <li><strong>EMC</strong> access with the necessary permissions;</li>
            <li>The <strong>.dll extension app</strong> provided by the Stream team;</li>
            <li>A valid <strong>ScreenId</strong> supplied by the Stream team;</li>
            <li>Access to Stream-Editor (Your company&apos;s environment and login);</li>
          </ul>
        </Article>
      </WikiSection>

      <WikiSection id="stream-extension-app" title="A - SET UP STREAM EXTENSION APP ON ORACLE EMC">
        <Article>
          <h3>Download and Install the Enterprise Management Console (EMC)</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>You can download and install <strong>EMC</strong> using the official Oracle documentation below:</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>EMC Download &amp; Installation guide. (Available <a href="#" target="_blank" rel="noopener noreferrer">here</a>)</p>
          </div>

          <h3>Access the Extension Application in EMC</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Open the <strong>EMC</strong> dashboard and sign in using your Oracle credentials.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/oracle-01.png" alt="EMC login screen" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Select the desired location, navigate to the <strong>Setup tab</strong> and click <strong>Extension Application</strong>.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/oracle-03.png" alt="Setup tab and Extension Application" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>From the top bar, click <strong>insert new record</strong>.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>When the popup appears, enter <strong>&quot;StreamExtensionApp&quot;</strong> as the record name, then confirm to create the entry. (The name must match exactly for the extension to work.)</p>
          </div>
          <div className="wiki-image-flow">
            <img src="/static/wiki/oracle/oracle-04.png" alt="Insert new record" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/oracle/oracle-05.png" alt="Create StreamExtensionApp entry" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>After selecting the newly added record, double click on the blank space row, at StreamExtensionApp and press <strong>Insert Application Content</strong>.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/oracle/oracle-06.png" alt="Select StreamExtensionApp" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/oracle/oracle-07.png" alt="Insert Application Content" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>You&apos;ll be asked to insert the ZoneableKey and Description. This new record name will be <strong>&quot;StreamExtensionApp&quot;</strong>.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/oracle-08.png" alt="ZoneableKey and Description dialog" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Select <strong>&quot;3 - DLL&quot;</strong> as Content Type, then press <strong>Import from a file</strong> and select the <strong>.dll file</strong> provided by the Stream team.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/oracle/oracle-09.png" alt="Select DLL content type" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/oracle/oracle-10.png" alt="Import DLL file" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Go back to the <strong>General</strong> tab and insert <strong>&quot;StreamExtensionApp.dll&quot;</strong> in the Disk file name column.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/oracle-11.png" alt="General tab with Disk file name" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>In the same <strong>General</strong> page, click <strong>&quot;Insert Application Content&quot;</strong>. This new record name will be <strong>&quot;streamSettings&quot;</strong>.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/oracle-12.png" alt="Insert Application Content for streamSettings" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Select <strong>&quot;23 - Text&quot;</strong> as content type. In the content copy and paste the next text/json format, with making the necessary changes, and changing the value of <strong>&quot;screenId&quot;</strong> property with the value provided by Stream team. Make sure that lower type value matches the order type that you wish to show up at the drive thru station.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/oracle/oracle-13.png" alt="Select Text content type" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/oracle/oracle-14.png" alt="Content configuration" className="wiki-screenshot" />
          </div>

          <h3>Settings format:</h3>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/oracle-15.png" alt="JSON settings format" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>The final result should look like the following:</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/oracle-16.png" alt="Final configuration result" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Finally make sure to <strong>SAVE</strong> everything clicking on the save icon, reload the database on the symphony client and then restart it.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/oracle-17.png" alt="Save and restart" className="wiki-screenshot" />
          </div>
        </Article>
      </WikiSection>

      <WikiSection id="duo-lane-configuration" title="B - SET UP DUO LANE CONFIGURATION ON ORACLE EMC">
        <Article>
          <h3>Access the Extension Application in EMC</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>To configure a <strong>Dual-lane</strong> setup at a single location, the process is straightforward. It follows the same basic steps as a standard configuration, with just a few key differences.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Open the <strong>EMC dashboard</strong>.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Select the desired location, navigate to the <strong>Setup tab</strong> and click <strong>Extension Application</strong>.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/oracle-17.png" alt="Setup tab for dual-lane" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>From the top bar, click <strong>insert new record</strong>.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>When the popup appears, enter <strong>&quot;streamExtApp&quot;</strong> as the record name, then confirm to create the entry. (This name must match exactly for the extension to work.)</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/oracle/oracle-18.png" alt="Insert new record for second lane" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/oracle/oracle-19.png" alt="Create streamExtApp entry" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>After selecting the newly added record, <strong>double click</strong> on the blank space row, at streamExtApp and press <strong>Insert Application Content</strong>.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/oracle/oracle-20.png" alt="Select streamExtApp" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/oracle/oracle-21.png" alt="Insert Application Content" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>You&apos;ll be asked to insert the ZoneableKey and Description. Insert on the <strong>Zoneable Key: streamExtApp</strong> and on the <strong>Description</strong> as you want: <strong>Second Lane</strong> for example.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/oracle-22.png" alt="ZoneableKey and Description for second lane" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Select <strong>&quot;3 - DLL&quot;</strong> as content type, then press <strong>import from file</strong> and select the <strong>.dll file</strong> provided by the Stream team.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/oracle/oracle-23.png" alt="Select DLL content type for second lane" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/oracle/oracle-24.png" alt="Import DLL file for second lane" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Go back to the <strong>General</strong> tab and insert <strong>&quot;streamExtApp.dll&quot;</strong> in the Disk file name column.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/oracle-25.png" alt="General tab with streamExtApp.dll" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Go back to the <strong>General</strong> tab and insert <strong>&quot;streamExtApp.dll&quot;</strong> in the Disk file name column.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/oracle-26.png" alt="Confirm Disk file name" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>In the same <strong>General</strong> page, click <strong>&quot;Insert Application Content&quot;</strong>. This new record name will be <strong>&quot;settings&quot;</strong>.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/oracle-27.png" alt="Insert Application Content for settings" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Select <strong>&quot;23 - Text&quot;</strong> as content type, then paste the JSON settings format template below into the content field;</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Update the values as needed, and replace the <strong>screenId</strong> with the value provided by the Stream team. Ensure the <strong>orderType</strong> matches the order type you need to display on the drive-thru station.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/oracle/oracle-28.png" alt="Select Text content type" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/oracle/oracle-29.png" alt="Content configuration for second lane" className="wiki-screenshot" />
          </div>

          <h3>Settings format:</h3>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/oracle-30.png" alt="JSON settings format for second lane" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>The final result should look like the following:</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/oracle-31.png" alt="Final configuration result for second lane" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Finally make sure to <strong>SAVE</strong> everything clicking on the save icon, reload the database on the symphony client and then restart it.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/oracle-32.png" alt="Save and restart for second lane" className="wiki-screenshot" />
          </div>
        </Article>
      </WikiSection>

      <WikiSection id="stream-editor-setup" title="C - MENU APP ONLY: ORACLE SETUP IN STREAM-EDITOR">
        <Article>
          <p>This section explains how to configure the Oracle integration specifically for the <strong>Menu App</strong> within Stream-Editor. Follow the steps below only if you are setting up the Menu App.</p>

          <h3>Log In to Stream-Editor</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Open your browser, navigate to your <strong>Stream-Editor URL</strong>, and log in with your credentials.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Click at <strong>OCB Settings</strong>.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Under <strong>Locations</strong>, choose a location or create a new one and click on the <strong>⚙️ gear icon</strong> next to the location to open the Integrations panel.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/oracle/oracle-38.png" alt="OCB Settings navigation" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/oracle/oracle-39.png" alt="Location settings with gear icon" className="wiki-screenshot" />
          </div>

          <h3>Connect Oracle</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>From the the Integrations panel list, select <strong>Oracle</strong> and click on the <strong>Next</strong> button.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/oracle/oracle-40.png" alt="Select Oracle integration" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/oracle/oracle-41.png" alt="Create Integration Next button" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Provide the required Oracle POS details in each field, based on <strong>your organization&apos;s Oracle configuration</strong>.</p>
          </div>

          <div className="wiki-fields-grid">
            <div className="wiki-fields-column">
              <ul className="wiki-list">
                <li><strong>POS Client ID:</strong> The Client ID provided by Oracle POS. This is a unique identifier for your application to authenticate and interact with the Oracle POS API.</li>
                <li><strong>POS Authentication URL:</strong> The URL endpoint used for authentication with Oracle POS. This URL is necessary to obtain access tokens required for API requests.</li>
                <li><strong>POS Base URL:</strong> The base URL of the Oracle POS API. All API requests will be appended to this URL to interact with the POS system.</li>
                <li><strong>POS Username:</strong> Your Oracle POS username. This is used along with the password to authenticate API requests.</li>
                <li><strong>POS Password:</strong> Your Oracle POS password. It is used with the username to securely access the POS API.</li>
                <li><strong>POS Organization ID:</strong> The unique identifier of your organization as registered with Oracle POS. This helps identify the organization within the POS system.</li>
                <li><strong>POS Location Reference:</strong> The reference code for the specific location within your organization. This ensures that the integration targets the right physical location.</li>
                <li><strong>POS Revenue Center Reference:</strong> The reference code for the revenue center within the location. It is used to specify which part of the location&apos;s operations the data should be pulled from or pushed to.</li>
              </ul>
            </div>

            <div className="wiki-modal-column">
              <img src="/static/wiki/oracle/oracle-42.png" alt="Oracle integration form with all fields" className="wiki-modal-screenshot" />
            </div>
          </div>

          <ul className="wiki-list">
            <li><strong>POS Display Name:</strong> The name that will be displayed for the POS system within your application. This should be user-friendly and easily recognizable.</li>
            <li><strong>POS Display Price Name:</strong> The name to be used for displaying price-related information from the POS system. This should match the naming convention for prices in your application or as needed for clarity.</li>
          </ul>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Once all fields are filled in, click on the <strong>Create</strong> button to complete the integration.</p>
          </div>
        </Article>
      </WikiSection>
    </HelpCenterContent>
  )
}

export default OracleSetup
